import { Appointment, TaskListItem, TimeManagementListItem } from '@myapp/contract'
import { getTopPx, getHeightPx } from './calendarConstants'

/* ------------------------------------------------------------------ */
/*  Rect extraction — mirrors the same logic each block component uses */
/* ------------------------------------------------------------------ */

export interface CalendarRect {
  id: string
  topPx: number
  heightPx: number
}

export function getAppointmentRect(appt: Appointment): CalendarRect {
  const isCompleted = appt.status === 'COMPLETED' || appt.status === 'COMPLETED_WITH_FOLLOW_UP'
  const hasActual = isCompleted && appt.actualStart != null && appt.actualTravelDuration != null && appt.actualWorkDuration != null

  const travel = hasActual ? appt.actualTravelDuration! : appt.plannedTravelDuration
  const work = hasActual ? appt.actualWorkDuration! : appt.plannedWorkDuration
  const departure = hasActual && appt.actualDepartureDuration != null ? appt.actualDepartureDuration : appt.plannedDepartureDuration

  const workStart = hasActual ? new Date(appt.actualStart!) : new Date(appt.plannedStart)
  const anfahrtStart = new Date(workStart.getTime() - travel * 60_000)

  return {
    id: appt.id,
    topPx: getTopPx(anfahrtStart.toISOString()),
    heightPx: Math.max(getHeightPx(travel + work + departure), 20),
  }
}

export function getTaskRect(task: TaskListItem): CalendarRect | null {
  if (!task.plannedStart) return null

  const isCompleted = task.status === 'COMPLETED'
  const hasActual = isCompleted && task.actualStart != null && task.actualTravelDuration != null && task.actualWorkDuration != null

  const travel = hasActual ? task.actualTravelDuration! : task.plannedTravelDuration
  const work = hasActual ? task.actualWorkDuration! : task.plannedWorkDuration
  const departure = hasActual && task.actualDepartureDuration != null ? task.actualDepartureDuration : task.plannedDepartureDuration

  const workStart = hasActual ? new Date(task.actualStart!) : new Date(task.plannedStart)
  const travelStart = new Date(workStart.getTime() - travel * 60_000)

  return {
    id: String(task.id),
    topPx: getTopPx(travelStart.toISOString()),
    heightPx: Math.max(getHeightPx(travel + work + departure), 20),
  }
}

export function getTMRect(entry: TimeManagementListItem, dayIso: string, shiftStart?: string | null, shiftEnd?: string | null): CalendarRect {
  const dayStart = new Date(`${dayIso}T00:00:00`)
  const dayEnd = new Date(`${dayIso}T23:59:59.999`)

  const start = new Date(entry.startTime)
  const end = new Date(entry.endTime)
  const allDay =
    start.toISOString().slice(0, 10) <= dayIso &&
    start.getHours() === 0 && start.getMinutes() === 0 &&
    end.toISOString().slice(0, 10) >= dayIso &&
    end.getHours() >= 23

  const clampedStart = allDay && shiftStart
    ? new Date(`${dayIso}T${new Date(shiftStart).toISOString().slice(11, 19)}`)
    : new Date(Math.max(new Date(entry.startTime).getTime(), dayStart.getTime()))

  const clampedEnd = allDay && shiftEnd
    ? new Date(`${dayIso}T${new Date(shiftEnd).toISOString().slice(11, 19)}`)
    : new Date(Math.min(new Date(entry.endTime).getTime(), dayEnd.getTime()))

  const durationMinutes = Math.max(0, Math.round((clampedEnd.getTime() - clampedStart.getTime()) / 60_000))

  return {
    id: String(entry.id),
    topPx: getTopPx(clampedStart.toISOString()),
    heightPx: Math.max(getHeightPx(durationMinutes), 14),
  }
}

export function getBreakRect(id: string, startTimeIso: string): CalendarRect {
  return {
    id,
    topPx: getTopPx(startTimeIso),
    heightPx: Math.max(getHeightPx(30), 14),
  }
}

/* ------------------------------------------------------------------ */
/*  Overlap layout — Notion-style cascading                           */
/* ------------------------------------------------------------------ */

const INDENT_PX = 16

export interface BlockLayout {
  /** CSS left value, e.g. '2px' or '18px' */
  left: string
  /** z-index — deeper = on top */
  zIndex: number
}

/**
 * Assigns a column (depth) to each rect so overlapping blocks cascade
 * to the right with increasing z-index (like Notion calendar).
 */
export function computeOverlapLayout(rects: CalendarRect[]): Map<string, BlockLayout> {
  if (rects.length === 0) return new Map()

  const sorted = [...rects].sort((a, b) => a.topPx - b.topPx || a.heightPx - b.heightPx)

  // columns[depth] = bottomPx of the last rect placed in that column
  const columns: number[] = []
  const depthMap = new Map<string, number>()

  for (const rect of sorted) {
    // Find the first column where this rect doesn't overlap
    let depth = 0
    while (depth < columns.length && columns[depth] > rect.topPx) {
      depth++
    }
    if (depth === columns.length) {
      columns.push(0)
    }
    columns[depth] = rect.topPx + rect.heightPx
    depthMap.set(rect.id, depth)
  }

  const result = new Map<string, BlockLayout>()
  for (const rect of rects) {
    const depth = depthMap.get(rect.id) ?? 0
    result.set(rect.id, {
      left: depth === 0 ? '2px' : `${2 + depth * INDENT_PX}px`,
      zIndex: 10 + depth,
    })
  }

  return result
}
